// ---------------------------------------------------------------------------
// From a player's Hive to a war, and from a war's end back to the player
// ---------------------------------------------------------------------------
//
// A war works from a copy of both Hives taken when it starts: damage happens
// in the war and nothing carries back. The war also snapshots the numbers it
// runs under (warRules), so retuning the sheet or upgrading a structure
// mid-war never changes a war already running.
//
// Kills to win: both players race to the LOWER of their two arenas' numbers.
// Nothing ever blocks going to war: a Hive with fewer structures than that
// opens the war with a placement step (warReadiness lists what to add), and
// whatever the player places stays on the Hive afterwards.

import { makeHomefront, placeStructure as stampStructure, isStructureDestroyed } from '../subplotGrid.js'
import { DEFAULT_PROGRESSION } from './config.js'
import { checkHive, placedFootprints } from './hive.js'

export function warKillsToWin(arenaA, arenaB, p = DEFAULT_PROGRESSION) {
    return Math.min(p.arenas[arenaA].killsToWin, p.arenas[arenaB].killsToWin)
}

// A test war on generated Hives (botHive.generatedPlayer) races to half the
// structures each side fields, rounded down: the arenas table's own pairing
// (2 or 3 structures → 1 kill, 4 → 2), carried on past it, between 1 and
// GENERATED_MAX_KILLS.
export const GENERATED_MAX_KILLS = 3
export function generatedKillsToWin(structures) {
    return Math.max(1, Math.min(GENERATED_MAX_KILLS, Math.floor(structures / 2)))
}

// The rules config (rules/config.js keys) a war runs under, fixed at its start.
export function warRules(killsToWin, p = DEFAULT_PROGRESSION) {
    return {
        structuresToWin: killsToWin,
        yieldPerLotPerRound: p.inputs.yieldPerLotPerRound,
        extractorYieldMultiplier: p.inputs.extractorYieldMultiplier,
        rocketCost: p.inputs.rocketCostYield,
        droneCost: p.inputs.reconCostYield,
    }
}

// Whether the player's Hive can go to a war needing `killsToWin` kills.
// Returns { placed, needed, short, ready, unplaced, problems }: `short` is how
// many more structures the placement step must add, `unplaced` the owned
// structures it can choose from (inventory order).
export function warReadiness(player, killsToWin, p = DEFAULT_PROGRESSION) {
    const placed = Object.keys(player.hive.structures).length
    const short = Math.max(0, killsToWin - placed)
    const unplaced = Object.keys(player.structures).filter(n => !player.hive.structures[n])
    const problems = checkHive(player, p)
    if (short > unplaced.length) problems.push(`${killsToWin} structures needed; only ${placed + unplaced.length} owned`)
    return { placed, needed: killsToWin, short, ready: short === 0 && problems.length === 0, unplaced, problems }
}

// The war's copy of a player's Hive: the 315-cell field with lots as each
// cell's resourceType, and each placed structure under its type name with what
// the war needs to know about it (level, and how it touches lot yield).
// Structures go in the Hive's placement order (structure key order is
// behaviour: it decides which rockets intercept first).
export function warHomefront(player, p = DEFAULT_PROGRESSION) {
    const problems = checkHive(player, p)
    if (problems.length) throw new Error(`Hive can't go to war: ${problems.join('; ')}`)
    const hf = makeHomefront(() => null)
    for (const [cell, type] of Object.entries(player.hive.lots)) hf.subplots[Number(cell)].resourceType = type
    const fps = placedFootprints(player, p)
    for (const name of Object.keys(player.hive.structures)) {
        const def = p.structures[name]
        stampStructure(hf, name, fps[name])
        Object.assign(hf.structures[name], {
            name,
            level: player.structures[name].level,
            extractorFor: def.extractorFor,
            stopsLotProduction: def.stopsLotProduction,
        })
    }
    return hf
}

// A player's war state at the end (resolveRound's player) → settleWarEnd's
// outcome. `result` is this player's: 'WIN' | 'LOSS' | 'TIE'.
export function warEndOutcome(warPlayer, result, { clockForfeit = false } = {}) {
    const hf = typeof warPlayer.homefront === 'string' ? JSON.parse(warPlayer.homefront) : warPlayer.homefront
    const survivorLevels = Object.values(hf?.structures ?? {})
        .filter(st => !isStructureDestroyed(hf, st.id))
        .map(st => st.level ?? 1)
    return {
        result,
        survivorLevels: clockForfeit ? [] : survivorLevels,
        leftoverYield: Math.max(0, warPlayer.yield ?? 0),
        yieldByType: { ...(warPlayer.yieldProduced ?? {}) },
        clockForfeit,
    }
}
